import React, { useState, useCallback, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import moment from "moment";
import { Spin, Alert } from "antd";

import CategorySummaryBar   from "./CategorySummaryBar";
import CompareFilterPanel   from "./CompareFilterPanel";
import CompareMap            from "./CompareMap";
import CompareLegend         from "./CompareLegend";
import ChangeSummaryPanel    from "./ChangeSummaryPanel";
import CompareStationPopup   from "./CompareStationPopup";
import CompareTimeseriesModal from "./CompareTimeseriesModal";
import CompareHelpDrawer     from "./CompareHelpDrawer";
import CompareTour           from "./CompareTour";
import { COMPARE_API_BASE }  from "./compareConstants";

import "./ComparePeriodsPage.css";

const DEFAULT_FILTERS = {
  periodType: "daily",
  metric:     "availability",
  varId:      "110",
  date1:      moment().subtract(2, "days").format("YYYY-MM-DD"),
  date2:      moment().subtract(1, "days").format("YYYY-MM-DD"),
  centers:    "all",
  region:     null,
  country:    null,
};

function readFiltersFromUrl() {
  const p = new URLSearchParams(window.location.search);
  if (!p.has("date1") && !p.has("metric") && !p.has("period_type")) return null;
  return {
    periodType: p.get("period_type") || DEFAULT_FILTERS.periodType,
    metric:     p.get("metric")      || DEFAULT_FILTERS.metric,
    varId:      p.get("var_id")      || DEFAULT_FILTERS.varId,
    date1:      p.get("date1")       || DEFAULT_FILTERS.date1,
    date2:      p.get("date2")       || DEFAULT_FILTERS.date2,
    centers:    p.get("centers")     || DEFAULT_FILTERS.centers,
    region:     p.get("region")      || null,
    country:    p.get("country")     || null,
  };
}

function writeFiltersToUrl(filters) {
  const p = new URLSearchParams();
  p.set("period_type", filters.periodType);
  p.set("metric",      filters.metric);
  p.set("var_id",      filters.varId);
  p.set("date1",       filters.date1);
  p.set("date2",       filters.date2);
  p.set("centers",     Array.isArray(filters.centers) ? filters.centers.join(",") : filters.centers);
  if (filters.region)  p.set("region",  filters.region);
  if (filters.country) p.set("country", filters.country);
  window.history.replaceState(null, "", `${window.location.pathname}?${p.toString()}`);
}

function buildQuery(filters) {
  const params = new URLSearchParams({
    period_type: filters.periodType,
    metric:      filters.metric,
    var_id:      filters.varId,
    date1:       filters.date1,
    date2:       filters.date2,
    centers:     Array.isArray(filters.centers) ? filters.centers.join(",") : filters.centers,
  });
  if (filters.region)  params.append("region",  filters.region);
  if (filters.country) params.append("country", filters.country);
  return params.toString();
}

const ComparePeriodsPage = () => {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
      document.documentElement.style.overflow = "";
    };
  }, []);

  const [filters,   setFilters]   = useState(() => readFiltersFromUrl() || DEFAULT_FILTERS);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [geojson,   setGeojson]   = useState(null);
  const [summary,   setSummary]   = useState(null);
  const [topDeg,    setTopDeg]    = useState([]);
  const [topImp,    setTopImp]    = useState([]);
  const [appliedFilters, setAppliedFilters] = useState(null);

  const [selectedStation,    setSelectedStation]    = useState(null);
  const [selectedStationDetail, setSelectedStationDetail] = useState(null);
  const [stationDetailLoading,  setStationDetailLoading]  = useState(false);
  const [tsModalOpen,    setTsModalOpen]    = useState(false);
  const [tsModalStation, setTsModalStation] = useState(null);
  const [helpOpen,       setHelpOpen]       = useState(false);
  const [tourOpen,       setTourOpen]       = useState(false);

  const popupContainerRef = useRef(null);

  const fetchAll = useCallback(async (f) => {
    setLoading(true);
    setError(null);
    setGeojson(null);
    setSummary(null);
    setTopDeg([]);
    setTopImp([]);
    setSelectedStation(null);
    setSelectedStationDetail(null);

    const qs = buildQuery(f);
    try {
      const [mapRes, sumRes, degRes, impRes] = await Promise.all([
        fetch(`${COMPARE_API_BASE}/map/?${qs}`),
        fetch(`${COMPARE_API_BASE}/summary/?${qs}`),
        fetch(`${COMPARE_API_BASE}/top-changes/?${qs}&direction=degradation&limit=10`),
        fetch(`${COMPARE_API_BASE}/top-changes/?${qs}&direction=improvement&limit=10`),
      ]);

      if (!mapRes.ok) throw new Error(`Map API error ${mapRes.status}`);

      const [mapData, sumData, degData, impData] = await Promise.all([
        mapRes.json(), sumRes.json(), degRes.json(), impRes.json(),
      ]);

      setGeojson(mapData);
      setSummary(sumData);
      setTopDeg(degData.stations || []);
      setTopImp(impData.stations || []);
      setAppliedFilters({ ...f });
    } catch (e) {
      setError(e.message || "Failed to load comparison data");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleApply = useCallback(() => {
    writeFiltersToUrl(filters);
    fetchAll(filters);
  }, [filters, fetchAll]);

  useEffect(() => {
    const initial = readFiltersFromUrl() || DEFAULT_FILTERS;
    setFilters(initial);
    fetchAll(initial);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStationDetail = useCallback(async (stationId, mapProps) => {
    if (!appliedFilters || !stationId) return;
    setStationDetailLoading(true);
    setSelectedStationDetail(null);

    const qs = buildQuery(appliedFilters);
    try {
      const res  = await fetch(
        `${COMPARE_API_BASE}/station/?${qs}&station_id=${stationId}`
      );
      const data = await res.json();
      setSelectedStationDetail(data);

      if (popupContainerRef.current) {
        const html = document.createElement("div");
        popupContainerRef.current.innerHTML = "";
        popupContainerRef.current.appendChild(html);
        ReactDOM.render(
          <CompareStationPopup
            mapProps={mapProps}
            detail={data}
            meta={appliedFilters}
          />,
          html,
        );
        if (CompareMap.updatePopup) {
          CompareMap.updatePopup(html.outerHTML);
        }
      }
    } catch {
    } finally {
      setStationDetailLoading(false);
    }
  }, [appliedFilters]);

  const handleStationClick = useCallback((props, _coords) => {
    setSelectedStation(props);
    setSelectedStationDetail(null);

    const tempDiv = document.createElement("div");
    ReactDOM.render(
      <CompareStationPopup mapProps={props} detail={null} />,
      tempDiv,
    );
    if (CompareMap.updatePopup) {
      CompareMap.updatePopup(tempDiv.innerHTML);
    }
    popupContainerRef.current = tempDiv;

    if (props.station_id) {
      fetchStationDetail(props.station_id, props);
    }
  }, [fetchStationDetail]);

  const handlePanelStationSelect = useCallback((station) => {
    setSelectedStation(station);

    // Show loading popup immediately, positioned at the station on the map
    const tempDiv = document.createElement("div");
    ReactDOM.render(
      <CompareStationPopup mapProps={station} detail={null} />,
      tempDiv,
    );
    popupContainerRef.current = tempDiv;

    const feature = geojson && geojson.features.find(
      f => f.properties.wigosid === station.wigosid
    );
    if (feature && CompareMap.openPopup) {
      const [lng, lat] = feature.geometry.coordinates;
      CompareMap.openPopup([lng, lat], tempDiv.innerHTML);
    }

    if (station.station_id) {
      fetchStationDetail(station.station_id, station);
    }
  }, [fetchStationDetail, geojson]);

  // ── Event delegation for popup action buttons ─────────────────────────
  // Since the popup is injected as HTML (not React), we use document-level
  // click delegation to detect button clicks.
  useEffect(() => {
    const handler = (e) => {
      const btn = e.target.closest("[data-action]");
      if (!btn) return;
      const action = btn.getAttribute("data-action");
      if (action === "close") {
        if (CompareMap.closePopup) CompareMap.closePopup();
        return;
      }
      if (action === "timeseries" && selectedStation) {
        if (CompareMap.closePopup) CompareMap.closePopup();
        if (document.activeElement && document.activeElement.blur) {
          document.activeElement.blur();
        }
        setTsModalStation(selectedStation);
        setTsModalOpen(true);
      }
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, [selectedStation]);

  const metric = (appliedFilters || filters).metric;
  const varId  = (appliedFilters || filters).varId;

  const handleMetricToggle = useCallback((m) => {
    const next = { ...filters, metric: m };
    setFilters(next);
    if (appliedFilters) {
      const nextApplied = { ...appliedFilters, metric: m };
      writeFiltersToUrl(nextApplied);
      fetchAll(nextApplied);
    }
  }, [filters, appliedFilters, fetchAll]);

  const handleExport = useCallback(() => {
    if (!appliedFilters) return;
    const qs = buildQuery(appliedFilters);
    window.open(`${COMPARE_API_BASE}/map/?${qs}&format=csv`, "_blank");
  }, [appliedFilters]);

  const stationCount = geojson ? geojson.features.length : null;

  return (
    <div className="cpp-root">
      {/* ── Top header ── */}
      <div className="cpp-page-header">
        <span className="cpp-page-title">
          Temporal Status Comparison (Date Diff View)
        </span>
        <div className="cpp-metric-group">
          <span className="cpp-metric-label">Compare by</span>
          <div className="cpp-metric-buttons">
            {["availability", "timeliness", "quality"].map(m => (
              <button
                key={m}
                className={`cpp-metric-btn${metric === m ? " cpp-metric-btn--active" : ""}`}
                onClick={() => handleMetricToggle(m)}
              >
                {m.charAt(0).toUpperCase() + m.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <button className="cpp-export-btn" onClick={handleExport} disabled={!geojson}>
          ↓ Export
        </button>
        <button className="cpp-help-btn cpp-help-btn--text" onClick={() => setTourOpen(true)} title="Start a guided tour of this page">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 5, verticalAlign: "middle" }}>
            <circle cx="12" cy="12" r="10" />
            <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
          </svg>
          Take Tour
        </button>
        <button className="cpp-help-btn cpp-help-btn--text" onClick={() => setHelpOpen(true)} title="Open help panel">
          Help
        </button>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          closable
          onClose={() => setError(null)}
          className="cpp-error"
        />
      )}

      {/* ── Body: filter | center-col (kpi + map) | change-summary ── */}
      <div className="cpp-body">
        <CompareFilterPanel
          filters={filters}
          onChange={setFilters}
          onApply={handleApply}
          loading={loading}
        />

        <div className="cpp-center-col">
          <CategorySummaryBar summary={summary} metric={metric} varId={varId} />

          <div className="cpp-map-area">
            {loading && (
              <div className="cpp-spinner-overlay">
                <Spin size="large" tip="Computing comparison…" />
              </div>
            )}
            <CompareMap
              geojson={geojson}
              onStationClick={handleStationClick}
              selectedWigosid={selectedStation ? selectedStation.wigosid : null}
            />
            {stationCount !== null && (
              <div className="cpp-map-footer">
                Showing {stationCount.toLocaleString()} stations
              </div>
            )}
            <div className="cpp-map-legend">
              <CompareLegend metric={metric} varId={varId} />
            </div>
          </div>
        </div>

        <ChangeSummaryPanel
          topDegraded={topDeg}
          topImproved={topImp}
          loading={loading}
          onStationSelect={handlePanelStationSelect}
          selectedWigosid={selectedStation ? selectedStation.wigosid : null}
        />
      </div>


      <div ref={popupContainerRef} style={{ display: "none" }} />

      <CompareTimeseriesModal
        visible={tsModalOpen}
        onClose={() => setTsModalOpen(false)}
        stationId={tsModalStation ? tsModalStation.station_id : null}
        stationName={tsModalStation ? tsModalStation.name : null}
        metric={(appliedFilters || filters).metric}
        filters={appliedFilters || filters}
      />

      <CompareHelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} />
      <CompareTour open={tourOpen} onClose={() => setTourOpen(false)} />
    </div>
  );
};

export default ComparePeriodsPage;
